// Optimistic cart panel: shown on the product page the moment Add-to-cart is
// clicked, while /cart/add.js is still in flight. The new line is marked as
// pending until the server confirms it; checkout stays disabled in the panel.
// The real /cart document is only requested after confirmation, so the panel
// never claims an item the server has not accepted.
window.DomaOptimisticCart = window.DomaOptimisticCart || (() => {
  let snapshot = null;
  let snapshotPromise = null;
  const money = (cents) => {
    try { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: window.Shopify?.currency?.active || 'BRL' }).format(cents / 100); }
    catch (error) { return 'R$ ' + (cents / 100).toFixed(2).replace('.', ','); }
  };
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const thumb = (url) => (url ? String(url).replace(/([?&])width=\d+/, '$1width=176') : '');

  function refreshSnapshot() {
    if (snapshotPromise) return snapshotPromise;
    const root = window.Shopify?.routes?.root || '/';
    snapshotPromise = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => { if (cart && Array.isArray(cart.items)) snapshot = { at: Date.now(), cart }; })
      .catch(() => {})
      .finally(() => { snapshotPromise = null; });
    return snapshotPromise;
  }

  function scheduleSnapshot() {
    const run = () => refreshSnapshot();
    const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(run, { timeout: 4000 }) : setTimeout(run, 1500));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  function injectStyles() {
    if (document.getElementById('doma-optimistic-cart-style')) return;
    const style = document.createElement('style');
    style.id = 'doma-optimistic-cart-style';
    style.textContent = `
.doma-oc{position:fixed;inset:0;z-index:2147483000;display:flex;justify-content:flex-end;background:rgba(0,0,0,.45);font-family:inherit}
.doma-oc__panel{width:min(440px,100%);height:100%;background:#fff;color:#121212;display:flex;flex-direction:column;box-shadow:-8px 0 30px rgba(0,0,0,.25)}
.doma-oc__head{display:flex;align-items:center;justify-content:space-between;padding:16px 18px;background:#050505;color:#fff;border-bottom:2px solid #bc962e}
.doma-oc__head h2{margin:0;font-size:18px;letter-spacing:.02em;color:#fff}
.doma-oc__close{background:none;border:0;color:#fff;font-size:28px;line-height:1;min-width:44px;min-height:44px;cursor:pointer}
.doma-oc__list{flex:1;overflow:auto;padding:8px 18px;margin:0;list-style:none}
.doma-oc__item{display:grid;grid-template-columns:64px 1fr;gap:12px;padding:12px 0;border-bottom:1px solid #eee}
.doma-oc__item img{width:64px;height:64px;object-fit:contain;background:#f6f6f6;border-radius:6px}
.doma-oc__title{font-size:14px;font-weight:600;line-height:1.3;margin:0 0 4px}
.doma-oc__meta{font-size:12px;color:#555;margin:0}
.doma-oc__state{display:inline-flex;align-items:center;gap:6px;margin-top:6px;font-size:12px;font-weight:600;padding:3px 8px;border-radius:999px}
.doma-oc__state--pending{background:#fff6dd;color:#7a5a00}
.doma-oc__state--pending::before{content:"";width:10px;height:10px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:doma-oc-spin .7s linear infinite}
.doma-oc__state--ok{background:#e3f6ef;color:#00664f}
.doma-oc__state--error{background:#fde8e8;color:#9b1c1c}
.doma-oc__foot{padding:14px 18px 18px;border-top:1px solid #eee}
.doma-oc__status{font-size:13px;margin:0 0 10px;min-height:1.4em}
.doma-oc__checkout{width:100%;min-height:50px;border:0;border-radius:8px;background:#0ac79a;color:#fff;font-size:16px;font-weight:700;cursor:pointer}
.doma-oc__checkout[disabled]{opacity:.55;cursor:not-allowed}
.doma-oc__continue{display:block;width:100%;margin-top:8px;min-height:44px;background:none;border:0;text-decoration:underline;font-size:14px;cursor:pointer;color:#121212}
@keyframes doma-oc-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.doma-oc__state--pending::before{animation:none}}`;
    document.head.appendChild(style);
  }

  function pendingLine(form, formData) {
    const info = form.closest('product-info') || document;
    const title = (info.querySelector('.product__title h1, .product__title, h1') || document.querySelector('h1'))?.textContent.trim() || 'Produto';
    const priceEl = [...info.querySelectorAll('[id^="price-"] .price-item--sale, [id^="price-"] .price-item--regular, .price-item--sale, .price-item--regular')]
      .find((el) => /\d/.test(el.textContent));
    const image = info.querySelector('.product__media-item.is-active img, media-gallery li.is-active img, .product__media img');
    const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);
    const details = [];
    const variant = [...info.querySelectorAll('variant-selects select, variant-selects input:checked')].map((el) => el.value).filter(Boolean).join(' / ');
    if (variant) details.push(variant);
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[([^_][^\]]*)\]$/.exec(key);
      if (match && String(value).trim()) details.push(`${match[1].replace(/\s*\(Opcional\)\s*/i, '')}: ${String(value).trim()}`);
    }
    return {
      title,
      price: priceEl ? priceEl.textContent.trim().replace(/\s+/g, ' ') : '',
      image: thumb(image?.currentSrc || image?.src || ''),
      quantity,
      details,
    };
  }

  function open(form, formData, handlers) {
    injectStyles();
    document.querySelector('.doma-oc')?.remove();
    const line = pendingLine(form, formData);
    const known = snapshot && Date.now() - snapshot.at < 10 * 60 * 1000 ? snapshot.cart.items.filter((item) => !item.properties?._doma_brinde) : [];
    const root = document.createElement('div');
    root.className = 'doma-oc';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'DomaOcTitle');
    root.innerHTML = `
<div class="doma-oc__panel">
  <div class="doma-oc__head"><h2 id="DomaOcTitle" tabindex="-1">Seu carrinho</h2><button type="button" class="doma-oc__close" aria-label="Fechar e continuar comprando">&times;</button></div>
  <ul class="doma-oc__list">
    <li class="doma-oc__item" data-pending>
      ${line.image ? `<img src="${escape(line.image)}" alt="" width="64" height="64">` : '<span></span>'}
      <div>
        <p class="doma-oc__title">${escape(line.title)}</p>
        <p class="doma-oc__meta">${escape(line.quantity)} × ${escape(line.price)}</p>
        ${line.details.map((d) => `<p class="doma-oc__meta">${escape(d)}</p>`).join('')}
        <span class="doma-oc__state doma-oc__state--pending" data-state role="status">Confirmando com a loja…</span>
      </div>
    </li>
    ${known.map((item) => `
    <li class="doma-oc__item">
      ${item.image ? `<img src="${escape(thumb(item.image + (item.image.includes('?') ? '&' : '?') + 'width=176'))}" alt="" width="64" height="64" loading="lazy">` : '<span></span>'}
      <div><p class="doma-oc__title">${escape(item.product_title)}</p><p class="doma-oc__meta">${escape(item.quantity)} × ${escape(money(item.final_price))}</p></div>
    </li>`).join('')}
  </ul>
  <div class="doma-oc__foot">
    <p class="doma-oc__status" data-status aria-live="polite">Adicionando o produto. Não é necessário clicar novamente.</p>
    <button type="button" class="doma-oc__checkout" data-checkout disabled>Finalizar compra</button>
    <button type="button" class="doma-oc__continue" data-continue>Continuar comprando</button>
  </div>
</div>`;
    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      root.remove();
      document.removeEventListener('keydown', onKey);
      handlers?.onClose?.();
    };
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    root.addEventListener('click', (event) => {
      if (event.target === root || event.target.closest('.doma-oc__close, [data-continue]')) close();
    });
    root.querySelector('[data-checkout]').addEventListener('click', () => handlers?.onCheckout?.());
    document.addEventListener('keydown', onKey);
    document.body.appendChild(root);
    requestAnimationFrame(() => {
      if (closed) return;
      root.querySelector('#DomaOcTitle').focus({ preventScroll: true });
      requestAnimationFrame(() => { if (!closed && typeof performance?.mark === 'function') performance.mark('doma:cart-optimistic-visible'); });
    });
    const state = root.querySelector('[data-state]');
    const status = root.querySelector('[data-status]');
    const checkout = root.querySelector('[data-checkout]');
    return {
      get closed() { return closed; },
      close,
      confirmed() {
        if (typeof performance?.mark === 'function') performance.mark('doma:add-confirmed');
        state.className = 'doma-oc__state doma-oc__state--ok';
        state.textContent = 'Adicionado';
        status.textContent = 'Produto confirmado. Abrindo o carrinho completo para finalizar…';
        checkout.disabled = false;
        checkout.textContent = 'Ir para o carrinho';
      },
      failed(message) {
        state.className = 'doma-oc__state doma-oc__state--error';
        state.textContent = 'Não adicionado';
        status.textContent = message || 'A loja não aceitou esta adição. Nada foi cobrado.';
        checkout.disabled = true;
      },
      uncertain() {
        state.className = 'doma-oc__state doma-oc__state--pending';
        state.textContent = 'Conferindo o carrinho…';
        status.textContent = 'Não recebemos a confirmação. Abrindo o carrinho para conferir, sem adicionar de novo.';
        checkout.disabled = true;
      },
    };
  }

  scheduleSnapshot();
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelector('.doma-oc')?.remove();
    refreshSnapshot();
  });
  return { open, refreshSnapshot };
})();

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Add-to-cart starts on the click itself (capture phase), before the
        // tracking listeners on document run. The native submit is replaced by
        // a requestSubmit() after the cart panel has painted, so form-submit
        // trackers still receive the event, just without delaying the panel.
        document.addEventListener('click', this.onSubmitterClick.bind(this), true);
        this.form.addEventListener('click', this.onBuyNowClick.bind(this), true);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        this.hideErrors = this.dataset.hideErrors === 'true';
        window.addEventListener('pageshow', (event) => {
          if (!event.persisted) return;

          // Returning from Yever can restore this document from the back/forward
          // cache. The checkout script's page-scoped busy lock is not reset by
          // the browser, so Add-to-cart would otherwise be silently cancelled.
          window.yeverCheckoutBusy = false;
          window.yeverCheckoutStarted = false;
          const overlay = document.querySelector('.yeverOverlayer');
          if (overlay) overlay.style.display = 'none';
          const checkoutError = document.getElementById('yeverCheckoutError');
          if (checkoutError) checkoutError.hidden = true;

          if (this.isConnected) {
            this.navigationPending = false;
            this.optimisticView = null;
            const state = typeof window.yeverProductState === 'function' ? window.yeverProductState(this.form) : null;
            if (this.requestActive || state === 'adding') return;
            this.releaseSubmission();
          }
        });
      }

      onBuyNowClick(event) {
        const button = event.target.closest('.shopify-payment-button__button--unbranded');
        if (!button) return;
        if (window.yeverCheckoutBusy || this.requestPending || this.confirmationUncertain) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.confirmationUncertain) this.navigateToCart();
          return;
        }
        if (typeof window.buyProduct === 'function') {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.buyProduct.call(button, event);
        }
      }

      onSubmitterClick(event) {
        if (event.defaultPrevented || event.button > 0) return;
        const button = event.target.closest?.('button, input[type="submit"]');
        if (!button || button.type !== 'submit' || button.form !== this.form || button.disabled) return;
        if (button.closest('.shopify-payment-button')) return;
        // Let the browser show its own message for invalid required fields.
        if (typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) return;
        event.preventDefault();
        this.onSubmitHandler(event);
        const forwardSubmit = () => {
          if (typeof this.form.requestSubmit !== 'function') return;
          this.trackingSubmit = true;
          try { this.form.requestSubmit(button); } catch (error) { console.error(error); }
          finally { this.trackingSubmit = false; }
        };
        requestAnimationFrame(() => setTimeout(forwardSubmit, 0));
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        // Forwarded submit for tracking listeners only; the add already started.
        if (this.trackingSubmit) return;
        const state = typeof window.yeverProductState === 'function' ? window.yeverProductState(this.form) : null;
        if (state === 'adding' || this.requestActive) {
          this.setProgress('Ainda estamos confirmando. Não é necessário clicar novamente.');
          return;
        }
        if (state === 'uncertain') { this.navigateToCart(); return; }
        if (window.yeverCheckoutBusy) {
          const overlay = document.querySelector('.yeverOverlayer');
          const overlayVisible = overlay && (window.getComputedStyle
            ? window.getComputedStyle(overlay).display !== 'none'
            : overlay.style.display && overlay.style.display !== 'none');
          if (overlayVisible) {
            this.setProgress('O checkout está em andamento. Aguarde ou volte à loja antes de adicionar outra peça.');
            return;
          }
          // An Add-to-cart submission is a distinct customer intent. Recover a
          // stale checkout lock only when its blocking overlay is not visible.
          window.yeverCheckoutBusy = false;
          window.yeverCheckoutStarted = false;
        }
        // An interrupted response can still represent a committed cart write.
        // Inspect the cart instead of submitting the same item a second time.
        if (this.confirmationUncertain) { this.navigateToCart(); return; }
        if (this.requestPending || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.requestPending = true;
        this.requestActive = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');
        const spinner = this.submitButton.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');
        this.setProgress('Adicionando ao carrinho…');
        const formData = new FormData(this.form);
        const controller = new AbortController();
        let request;
        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;
          config.signal = controller.signal;
          const cartRoot = window.Shopify?.routes?.root || '/';
          request = fetch(`${cartRoot}cart/add.js`, config);
        } catch (error) {
          request = Promise.reject(error);
        }
        request.catch(() => {});
        let view = null;
        let confirmed = false;
        try {
          view = window.DomaOptimisticCart?.open(this.form, formData, {
            onClose: () => { if (this.optimisticView === view) this.optimisticView = null; },
            onCheckout: () => { if (confirmed) this.navigateToCart(); },
          }) || null;
        } catch (error) { console.error(error); view = null; }
        this.optimisticView = view;
        const viewOpen = () => view && !view.closed && this.optimisticView === view;
        const slowTimer = setTimeout(() => this.setProgress('Ainda estamos confirmando. Não é necessário clicar novamente.'), 6000);
        const timeout = setTimeout(() => controller.abort(), 12000);
        try {
          const result = await request;
          const response = await result.json().catch(() => null);
          if (!result.ok || response?.status) {
            if (result.status >= 500 || result.status === 408) throw new Error('Resposta do carrinho indisponível');
            if (!response) {
              this.handleErrorMessage('A loja não aceitou esta tentativa. Aguarde um momento antes de tentar novamente.');
              this.setProgress('Não foi possível adicionar o produto nesta tentativa.');
              if (viewOpen()) view.failed('A loja não aceitou esta tentativa. Aguarde um momento antes de tentar novamente.');
              return;
            }
            this.handleErrorMessage(response.description || window.cartStrings.error);
            this.setProgress('Não foi possível adicionar. Confira a mensagem acima antes de tentar novamente.');
            if (viewOpen()) view.failed(response.description || window.cartStrings.error);
            if (typeof publish === 'function') {
              try { Promise.resolve(publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: formData.get('id'), errors: response.errors || response.description, message: response.message })).catch(console.error); } catch (error) { console.error(error); }
            }
            return;
          }
          if (!response || (!response.id && !response.items)) throw new Error('Resposta de adição inválida');
          confirmed = true;
          if (this.cart && typeof publish === 'function') {
            try { Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response })).catch(console.error); } catch (error) { console.error(error); }
          }
          if (view && !viewOpen()) {
            // The customer closed the panel to keep shopping; respect that.
            window.DomaOptimisticCart?.refreshSnapshot?.();
            this.setProgress('Produto adicionado.', true);
            return;
          }
          if (view) view.confirmed();
          this.setProgress('Produto adicionado. Abrindo seu carrinho…', true);
          this.navigateToCart();
          return;
        } catch (error) {
          console.error(error);
          this.confirmationUncertain = !confirmed;
          this.setProgress(confirmed ? 'Produto adicionado. Acesse seu carrinho para continuar.' : 'Não foi possível confirmar a adição. Confira seu carrinho antes de tentar novamente.', true);
          // The server may have accepted the product before the connection failed.
          // Navigation is read-only and preserves quantity and engraving.
          if (viewOpen()) view.uncertain();
          if (view && !viewOpen() && !confirmed) return;
          try { this.navigateToCart(); } catch (navigationError) { console.error(navigationError); }
        } finally {
          this.requestActive = false;
          clearTimeout(slowTimer);
          clearTimeout(timeout);
          if (!this.navigationPending) {
            this.releaseSubmission();
          }
        }
      }

      releaseSubmission() {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.querySelector('.loading__spinner')?.classList.add('hidden');
        this.requestPending = false;
        this.submitButton.removeAttribute('aria-disabled');
      }

      navigateToCart() {
        this.navigationPending = true;
        try { window.location.assign(window.routes.cart_url); }
        catch (error) { this.navigationPending = false; throw error; }
      }

      setProgress(message, cartLink = false) {
        if (!this.progressMessage) {
          this.progressMessage = document.createElement('p');
          this.progressMessage.className = 'product-form__status caption-large';
          this.progressMessage.setAttribute('role', 'status');
          this.progressMessage.setAttribute('aria-live', 'polite');
          this.form.after(this.progressMessage);
        }
        this.progressMessage.textContent = message;
        if (cartLink) {
          const link = document.createElement('a');
          link.href = window.routes.cart_url;
          link.textContent = ' Ver carrinho';
          this.progressMessage.append(link);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
